/** Enemy entity: placeholder sprite with physics, health, AI, and a facing direction pip. */
import Phaser from 'phaser';
import { Entity } from './Entity';
import { PhysicsComponent } from '../components/PhysicsComponent';
import { HealthComponent } from '../components/HealthComponent';
import { AIComponent } from '../components/AIComponent';
import type { EnemyArchetypeConfig, PlayerHideState } from '../utils/Types';
import { EnemyBackupSkillEffect, EnemyRangedSkillEffect } from '../skills/enemy';
import type { BackupAlertCallback, ProjectileSpawner } from '../skills/enemy';
import { buildSkillRegistry } from '../skills/enemy/EnemySkillFactory';
import { crispRect, crispGraphics, CRISP_SCALE } from '../ui/CrispUtil';
import { DebugLog } from '../debug/DebugLog';
import { DEFAULT_CORPSE_DURATION_MS } from '../ai/constants';
import { ConfigLoader } from '../systems/ConfigLoader';
import type { StatusEffectTracker } from '../systems/StatusEffectTracker';

// Facing indicator constants
const FACING_PIP_OFFSET = 0.55; // multiplied by half-size — how far from center the pip sits
const FACING_PIP_RADIUS = 2.5; // px — pip circle radius
const FACING_PIP_COLOR = 0xffffff;
const FACING_PIP_ALPHA = 0.9;

import { COLS_PER_ROW, COL_IDLE, COL_WALK1, COL_WALK2, COL_ATTACK_WINDUP, COL_ATTACK_STRIKE, COL_HIT, COL_DEATH, ROW_SOUTH, ROW_NORTH, ROW_EAST, ANIM_FRAME_RATE, facingToRow, ENEMY_LIGHT_BODY, ENEMY_HEAVY_BODY, SPRITE_DISPLAY_STANDARD, SPRITE_DISPLAY_HEAVY } from './SpriteLayout';

// How long the hit-reaction frame holds before resuming normal animation (ms).
// Intentionally longer than player HIT_FLASH_MS (150ms) — enemies telegraph hits more visibly.
const HIT_FLASH_DURATION_MS = 200;

/** An AI-controlled enemy with archetype-specific stats, a physics body, and AI orchestration. */
export class Enemy extends Entity {
  private physics: PhysicsComponent;
  private health: HealthComponent;
  ai: AIComponent;
  config: EnemyArchetypeConfig;
  /** Stable per-instance tag assigned by EnemyManager at spawn time. Used in logs and inspector. */
  instanceTag: string = '';
  isKnockedBack: boolean = false;
  /** Respawn coordinates set at spawn time, read at death time by EnemyManager. Never mutated after spawn. */
  respawnMeta: { respawnX?: number; respawnY?: number } | null = null;
  /** Scene-time timestamp until which the enemy is retreating (skips AI velocity). */
  retreatUntil: number = 0;
  /** Accumulated delta from throttled (skipped) AI frames, flushed on the next real AI tick. */
  throttledDelta: number = 0;
  /** When true, AI movement is suppressed — enemy holds position. Used by simulation bridge. */
  simFrozen: boolean = false;
  /** Per-entity status effect tracker. Created by EnemySpawner via StatusEffectSystem.createTracker(). */
  statusEffectTracker: StatusEffectTracker | null = null;
  private facingPip: Phaser.GameObjects.Graphics | null = null;
  private halfSize: number;
  private animSprite: Phaser.GameObjects.Sprite | null = null;
  private hitFlashTimer: number = 0;
  /** True while grapple_held/crush_hold tint is applied — avoids redundant setTint calls. */
  private bindTintActive: boolean = false;

  constructor(scene: Phaser.Scene, x: number, y: number, config: EnemyArchetypeConfig, groundLayer: Phaser.Tilemaps.TilemapLayer, instanceTag?: string) {
    const size = config.weight === 'heavy' || config.weight === 'immovable' ? 20 : 14;

    let gameObject: Phaser.GameObjects.Rectangle | Phaser.GameObjects.Sprite;
    if (config.spriteKey) {
      const s = scene.add.sprite(x, y, config.spriteKey, ROW_SOUTH * COLS_PER_ROW + COL_IDLE);
      // Scale sprite frame to display size. The character occupies ~30% of the
      // frame width, so we need a large display frame to get a ~32px visible character.
      const displaySize = config.weight === 'heavy' || config.weight === 'immovable' ? SPRITE_DISPLAY_HEAVY : SPRITE_DISPLAY_STANDARD;
      s.setScale(displaySize / s.frame.width);
      Enemy.createAnimations(scene, config.spriteKey);
      gameObject = s;
    } else {
      const color = ConfigLoader.getArchetypeColor(config.id);
      gameObject = crispRect(scene, x, y, size, size, color);
    }

    super(scene, gameObject);

    this.config = config;
    this.halfSize = size / 2;
    this.animSprite = config.spriteKey ? (gameObject as Phaser.GameObjects.Sprite) : null;

    // Facing direction indicator — only for placeholder rectangles
    if (!this.animSprite) {
      this.facingPip = crispGraphics(scene);
      this.facingPip.fillStyle(FACING_PIP_COLOR, FACING_PIP_ALPHA);
      this.facingPip.fillCircle(0, 0, FACING_PIP_RADIUS * CRISP_SCALE);
      this.facingPip.setPosition(x, y + this.halfSize * FACING_PIP_OFFSET);
    }

    this.physics = new PhysicsComponent(scene, gameObject);
    if (config.spriteKey) {
      const bodyGeo = (config.weight === 'heavy' || config.weight === 'immovable')
        ? ENEMY_HEAVY_BODY : ENEMY_LIGHT_BODY;
      this.physics.body.setSize(bodyGeo.sizeW, bodyGeo.sizeH);
      this.physics.body.setOffset(bodyGeo.offsetX, bodyGeo.offsetY);
    } else {
      this.physics.body.setSize(size, size);
    }
    this.physics.body.setDrag(150, 150);

    this.health = new HealthComponent(this.events, config.baseHp);
    this.ai = new AIComponent(config, x, y, groundLayer, buildSkillRegistry(config), instanceTag);

    this.addComponent('physics', this.physics);
    this.addComponent('health', this.health);
    this.addComponent('ai', this.ai);

    // Trigger hit-flash animation when taking damage (only for sprite-based enemies)
    if (this.animSprite) {
      this.events.on('health-changed', () => {
        if (!this.health.isDead) {
          this.hitFlashTimer = HIT_FLASH_DURATION_MS;
        }
      });
    }
  }

  /** Register all animations for a sprite sheet; no-op if already registered. */
  private static createAnimations(scene: Phaser.Scene, spriteKey: string): void {
    const southWalk = `${spriteKey}-walk-south`;
    if (scene.anims.exists(southWalk)) return;

    const f = (row: number, col: number) => ({ key: spriteKey, frame: row * COLS_PER_ROW + col });

    for (const [dir, row] of [['south', ROW_SOUTH], ['north', ROW_NORTH], ['east', ROW_EAST]] as const) {
      scene.anims.create({
        key: `${spriteKey}-walk-${dir}`,
        frames: [f(row, COL_IDLE), f(row, COL_WALK1), f(row, COL_IDLE), f(row, COL_WALK2)],
        frameRate: ANIM_FRAME_RATE,
        repeat: -1,
      });

      scene.anims.create({
        key: `${spriteKey}-attack-${dir}`,
        frames: [f(row, COL_ATTACK_WINDUP), f(row, COL_ATTACK_STRIKE)],
        frameRate: ANIM_FRAME_RATE,
        repeat: 0,
      });
    }
  }

  /** The Arcade physics body for collision and velocity control. */
  get body(): Phaser.Physics.Arcade.Body {
    return this.physics.body;
  }

  get healthComponent(): HealthComponent {
    return this.health;
  }

  /** IDs of status effects that amplify incoming damage. */
  private static readonly VULN_EFFECT_IDS = [
    'vulnerability', 'armor_shred', 'corrosion',
    'seizure_vulnerability', 'overripe_vulnerability', 'seared',
  ];

  /**
   * Compute the total incoming-damage multiplier from vulnerability/armor debuffs.
   * Returns 1 when no debuffs are active. Intensity-stacking effects sum across
   * all stacks; override effects contribute their single intensity value.
   * Capped at 1.9 (90% amplification) to mirror the 0.9 DR cap on players.
   */
  getVulnerabilityMultiplier(): number {
    const tracker = this.statusEffectTracker;
    if (!tracker) return 1;

    let total = 0;
    for (const id of Enemy.VULN_EFFECT_IDS) {
      const instances = tracker.getInstances(id);
      for (const inst of instances) {
        total += inst.intensity * inst.stacks;
      }
    }

    return total > 0 ? Math.min(1 + total, 1.9) : 1;
  }

  /** Run the AI tick, apply resulting velocity, and reposition the facing pip. */
  updateAI(playerX: number, playerY: number, delta: number, playerHideState?: PlayerHideState): void {
    if (this.simFrozen) { this.physics.setVelocity(0, 0); return; }
    if (this.isKnockedBack) return;
    if (this.scene.time.now < this.retreatUntil) return;

    // Stun/paralysis: full paralysis — skip AI tick, zero velocity, no facing/animation
    if (this.statusEffectTracker?.has('stun') || this.statusEffectTracker?.has('dread_paralysis')) {
      this.physics.setVelocity(0, 0);
      return;
    }

    const result = this.ai.getMovement(
      this.body.center.x, this.body.center.y,
      playerX, playerY,
      delta,
      playerHideState,
    );

    // Wall sliding: when the body is blocked on an axis (from last frame's
    // collision), zero velocity on that axis so the perpendicular component
    // slides the enemy past corners instead of fighting the physics.
    let vx = result.vx;
    let vy = result.vy;
    const blocked = this.body.blocked;
    const wasSliding = (blocked.left && vx < 0) || (blocked.right && vx > 0)
      || (blocked.up && vy < 0) || (blocked.down && vy > 0);
    if (blocked.left && vx < 0) vx = 0;
    if (blocked.right && vx > 0) vx = 0;
    if (blocked.up && vy < 0) vy = 0;
    if (blocked.down && vy > 0) vy = 0;

    if (wasSliding) {
      const dirs = [blocked.left && 'L', blocked.right && 'R', blocked.up && 'U', blocked.down && 'D'].filter(Boolean).join('');
      DebugLog.throttled(`wall-slide-${this.ai.tag}`, 500, 'ai',
        `[${this.ai.tag}] WALL-SLIDE blocked=${dirs} ai=(${Math.round(result.vx)},${Math.round(result.vy)}) → (${Math.round(vx)},${Math.round(vy)}) pos=(${Math.round(this.body.center.x)},${Math.round(this.body.center.y)})`);
    }

    // Root / immobilize: suppress all movement but allow facing and AI
    if (this.statusEffectTracker?.has('root')
      || this.statusEffectTracker?.has('grapple_held')
      || this.statusEffectTracker?.has('crush_hold')) {
      vx = 0;
      vy = 0;
    }

    // Slow: reduce movement speed by the strongest active slow multiplier.
    // Only applies when not already immobilized (root zeroes velocity above).
    if ((vx !== 0 || vy !== 0) && this.statusEffectTracker) {
      let slowest = 1.0;
      for (const effect of this.statusEffectTracker.getAll()) {
        if (effect.def.speedMultiplier !== undefined && effect.def.speedMultiplier < slowest) {
          slowest = effect.def.speedMultiplier;
        }
      }
      if (slowest < 1.0) {
        vx *= slowest;
        vy *= slowest;
      }
    }

    this.physics.setVelocity(vx, vy);

    // Update facing direction from movement velocity — use the AI's intended
    // direction so rooted enemies still face the player
    this.ai.updateFacing(result.vx, result.vy, delta, this.body.center.x, this.body.center.y);

    if (this.animSprite) {
      this.updateSpriteAnimation(result.vx, result.vy, delta);
    } else {
      // Position the facing pip on the leading edge of the placeholder rectangle
      const offset = this.halfSize * FACING_PIP_OFFSET;
      this.facingPip!.setPosition(
        this.sprite.x + this.ai.facingX * offset,
        this.sprite.y + this.ai.facingY * offset,
      );
      this.facingPip!.setDepth(this.sprite.depth + 1);
    }

    // Bind tint: steel-blue overlay while grapple_held or crush_hold is active
    const isHeld = !!(
      this.statusEffectTracker?.has('grapple_held') ||
      this.statusEffectTracker?.has('crush_hold')
    );
    if (isHeld !== this.bindTintActive) {
      this.bindTintActive = isHeld;
      const tintable = this.sprite as unknown as { setTint(c: number): void; clearTint(): void };
      if (isHeld) tintable.setTint(0x88CCFF);
      else tintable.clearTint();
    }
  }

  /** Resolve current facing to a sprite sheet row and flipX flag. */
  private getFacingRow(): { row: number; dir: string; flipX: boolean } {
    return facingToRow(this.ai.facingX, this.ai.facingY);
  }

  /** Update sprite animation based on AI state: attack > hit > walk > idle. */
  private updateSpriteAnimation(vx: number, vy: number, delta: number): void {
    const s = this.animSprite!;
    const spriteKey = this.config.spriteKey!;
    const { row, dir, flipX } = this.getFacingRow();

    // Tick down hit flash timer
    if (this.hitFlashTimer > 0) {
      this.hitFlashTimer -= delta;
    }

    // Priority 1: Hit flash overrides everything briefly
    if (this.hitFlashTimer > 0) {
      s.anims.stop();
      s.setFrame(row * COLS_PER_ROW + COL_HIT);
      s.setFlipX(flipX);
      return;
    }

    // Priority 2: Attack animation when a skill is in windup or active phase
    const activeSkill = this.ai.activeSkill;
    if (activeSkill?.isActive?.()) {
      const phase = (activeSkill as { phase?: string }).phase;
      if (phase === 'windup') {
        s.anims.stop();
        s.setFrame(row * COLS_PER_ROW + COL_ATTACK_WINDUP);
        s.setFlipX(flipX);
        return;
      }
      if (phase === 'active') {
        s.anims.stop();
        s.setFrame(row * COLS_PER_ROW + COL_ATTACK_STRIKE);
        s.setFlipX(flipX);
        return;
      }
    }

    // Priority 3: Walk or idle
    const isMoving = vx !== 0 || vy !== 0;
    if (!isMoving) {
      s.anims.stop();
      s.setFrame(row * COLS_PER_ROW + COL_IDLE);
      s.setFlipX(flipX);
    } else {
      s.play(`${spriteKey}-walk-${dir}`, true);
      s.setFlipX(flipX);
    }
  }

  /**
   * Spawn a lightweight corpse sprite at this enemy's position, then immediately
   * invoke the callback so the full Enemy entity can be removed from memory.
   * Returns duration and optional corpse sprite for external tracking.
   */
  playDeath(onComplete: () => void): { duration: number; corpseSprite: Phaser.GameObjects.Sprite | null } {
    const duration = this.config.corpseDurationMs ?? DEFAULT_CORPSE_DURATION_MS;
    let corpseSprite: Phaser.GameObjects.Sprite | null = null;

    if (this.animSprite) {
      const { row, flipX } = this.getFacingRow();
      const frame = row * COLS_PER_ROW + COL_DEATH;
      corpseSprite = this.scene.add.sprite(this.sprite.x, this.sprite.y, this.config.spriteKey!, frame);
      corpseSprite.setScale(this.animSprite.scaleX, this.animSprite.scaleY);
      corpseSprite.setFlipX(flipX);
      corpseSprite.setDepth(0); // Always render under living entities (which use y-sorting)
      corpseSprite.setAlpha(0.85);

      this.scene.time.delayedCall(duration, () => corpseSprite!.destroy());
    }

    onComplete();
    return { duration, corpseSprite };
  }

  /** Wire a backup alert callback into all registered backup skills. Called by EnemyManager after construction. */
  setBackupCallback(callback: BackupAlertCallback): void {
    for (const skillDef of this.config.skills) {
      if (skillDef.type !== 'backup') continue;
      const skill = this.ai.skillRegistry.getSkill(skillDef.id);
      if (skill instanceof EnemyBackupSkillEffect) {
        skill.setAlertCallback(callback);
      }
    }
  }

  /** Wire a projectile spawn callback into all registered ranged skills. Called by EnemyManager after construction. */
  setProjectileSpawner(spawner: ProjectileSpawner): void {
    for (const skillDef of this.config.skills) {
      if (skillDef.type !== 'ranged') continue;
      const skill = this.ai.skillRegistry.getSkill(skillDef.id);
      if (skill instanceof EnemyRangedSkillEffect) {
        skill.setSpawner(spawner);
      }
    }
  }

  /** Destroy the facing pip graphic and all inherited entity objects. */
  destroy(): void {
    this.statusEffectTracker?.removeAll();
    this.facingPip?.destroy();
    super.destroy();
  }
}
